import { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  Alert,
  Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { EmojiEvents, EventNote, AttachMoney, CheckCircle, RadioButtonUnchecked } from '@mui/icons-material';
import { listarConcursosAbertos } from '../../../api/concursos';
import useTenant from '../../../hooks/useTenant';
import { formatarPeriodo } from '../../../utils/formatacao';

function Step2EscolhaConcurso({ data, onChange, onAutoSelected }) {
  const { t } = useTranslation();
  const { selectedSlug } = useTenant();
  const [concursos, setConcursos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedSlug) return;
    setLoading(true);
    listarConcursosAbertos()
      .then((res) => {
        const list = res.data?.data ?? res.data;
        const arr = Array.isArray(list) ? list : [];
        setConcursos(arr);

        // Auto-seleciona se houver apenas 1 concurso e notifica o wizard pra pular este step
        if (arr.length === 1) {
          onChange({ ...data, concursoId: arr[0].concursoId ?? arr[0].id, concurso: arr[0] });
          if (onAutoSelected) onAutoSelected();
        }
      })
      .catch(() => setConcursos([]))
      .finally(() => setLoading(false));
  }, [selectedSlug]);

  const handleSelecionar = (concurso) => {
    const id = concurso.concursoId ?? concurso.id;
    onChange({ ...data, concursoId: id, concurso });
  };

  const isSelected = (concurso) => {
    const id = concurso.concursoId ?? concurso.id;
    return data.concursoId === id;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (concursos.length === 0) {
    return (
      <Box>
        <Typography variant="h5" fontWeight={600} gutterBottom textAlign="center">
          {t('wizard.step2.title')}
        </Typography>
        <Alert severity="info" sx={{ mt: 2 }}>
          {t('wizard.step2.noConcursos')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom textAlign="center">
        {t('wizard.step2.title')}
      </Typography>
      <Typography variant="body2" mb={4} textAlign="center">
        {t('wizard.step2.subtitle')}
      </Typography>

      <Grid container spacing={2} justifyContent="center">
        {concursos.map((concurso) => {
          const id = concurso.concursoId ?? concurso.id;
          const selecionado = isSelected(concurso);
          const taxa = concurso.taxaInscricao ?? concurso.taxa ?? null;
          const gratuito = !taxa || taxa === 0;
          const periodoInscricao = formatarPeriodo(concurso.periodoInicio, concurso.periodoFim);
          const periodoEvento = formatarPeriodo(concurso.dataInicio, concurso.dataFim);

          return (
            <Grid item xs={12} sm={6} key={id}>
              <Card
                sx={{
                  height: '100%',
                  border: 2,
                  borderColor: selecionado ? 'primary.main' : 'transparent',
                  bgcolor: (theme) => (selecionado ? alpha(theme.palette.primary.main, 0.04) : 'background.paper'),
                  transition: 'border-color 0.2s, background-color 0.2s',
                }}
              >
                <CardActionArea onClick={() => handleSelecionar(concurso)} sx={{ height: '100%' }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" spacing={1.5} alignItems="flex-start" mb={2}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: selecionado ? 'primary.main' : 'grey.100',
                          color: selecionado ? 'primary.contrastText' : 'text.secondary',
                        }}
                      >
                        <EmojiEvents fontSize="small" />
                      </Box>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1, lineHeight: 1.3, pt: 0.75 }}>
                        {concurso.nome ?? t('Contest')}
                      </Typography>
                      {selecionado ? (
                        <CheckCircle color="primary" />
                      ) : (
                        <RadioButtonUnchecked sx={{ color: 'text.disabled' }} />
                      )}
                    </Stack>

                    <Stack spacing={1}>
                      {periodoInscricao && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EventNote sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {t('wizard.step2.inscricoes')}: <b>{periodoInscricao}</b>
                          </Typography>
                        </Box>
                      )}
                      {periodoEvento && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EventNote sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {t('wizard.step2.evento')}: <b>{periodoEvento}</b>
                          </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AttachMoney sx={{ fontSize: 16, color: 'text.secondary' }} />
                        {gratuito ? (
                          <Chip label={t('wizard.step2.gratuito')} size="small" color="success" variant="outlined" />
                        ) : (
                          <Typography variant="body2">
                            {t('wizard.step2.valor')}: R${' '}
                            <b>
                              {Number(taxa).toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                              })}
                            </b>
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

export default Step2EscolhaConcurso;
